/**
 * useScores Hook
 *
 * React Query hooks for investment scoring operations
 *
 * Data Model:
 * - Scores (admin): Template scores in lookup table
 * - InvestmentScores: Cloned scores per investment with score_override
 * - ScoreCategories: Categories with selection_type (S=single, M=multiple)
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  scoresService,
  InvestmentScoresResponse,
  AvailableScoresResponse,
  AddScoreResponse,
  AddScoresBatchResponse,
  UpdateScoreResponse,
  DeleteScoreResponse,
} from '../services/scores.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

export const scoreKeys = {
  all: ['scores'] as const,
  investment: (investmentId: number) => [...scoreKeys.all, 'investment', investmentId] as const,
  available: (investmentId: number) => [...scoreKeys.all, 'available', investmentId] as const,
};

/**
 * Fetch investment scores grouped by category
 */
export function useInvestmentScores(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentScoresResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentScoresResponse, Error>({
    queryKey: scoreKeys.investment(investmentId),
    queryFn: async () => {
      const response = await scoresService.getInvestmentScores(investmentId);
      // API wraps response in { success, data, meta } - unwrap it
      return unwrapResponse(response.data);
    },
    enabled: investmentId > 0,
    ...options,
  });
}

/**
 * Fetch all available scores (from admin table) grouped by category
 */
export function useAvailableScores(
  investmentId: number,
  options?: Omit<UseQueryOptions<AvailableScoresResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<AvailableScoresResponse, Error>({
    queryKey: scoreKeys.available(investmentId),
    queryFn: async () => {
      const response = await scoresService.getAvailableScores(investmentId);
      // API wraps response in { success, data, meta } - unwrap it
      return unwrapResponse(response.data);
    },
    enabled: investmentId > 0,
    ...options,
  });
}

/**
 * Add a score to an investment (clone from admin Scores table)
 */
export function useAddScore(
  investmentId: number,
  options?: UseMutationOptions<AddScoreResponse, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<AddScoreResponse, Error, number>({
    mutationFn: async (scoreId) => {
      const response = await scoresService.addScore(investmentId, scoreId);
      // API wraps response in { success, data, meta } - unwrap it
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scoreKeys.investment(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Add multiple scores to an investment (for multi-selection categories)
 */
export function useAddScoresBatch(
  investmentId: number,
  options?: UseMutationOptions<AddScoresBatchResponse, Error, number[]>
) {
  const queryClient = useQueryClient();

  return useMutation<AddScoresBatchResponse, Error, number[]>({
    mutationFn: async (scoreIds) => {
      const response = await scoresService.addScoresBatch(investmentId, scoreIds);
      // API wraps response in { success, data, meta } - unwrap it
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scoreKeys.investment(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Replace a score in a category (for single-selection categories)
 */
export function useReplaceCategoryScore(
  investmentId: number,
  options?: UseMutationOptions<
    UpdateScoreResponse,
    Error,
    { scoreCategoryId: number; scoreId: number }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<UpdateScoreResponse, Error, { scoreCategoryId: number; scoreId: number }>({
    mutationFn: async ({ scoreCategoryId, scoreId }) => {
      const response = await scoresService.replaceCategoryScore(
        investmentId,
        scoreCategoryId,
        scoreId
      );
      // API wraps response in { success, data, meta } - unwrap it
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scoreKeys.investment(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Update score override for an investment score
 */
export function useUpdateScoreOverride(
  investmentId: number,
  options?: UseMutationOptions<
    UpdateScoreResponse,
    Error,
    { investmentScoreId: number; scoreOverride: number }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateScoreResponse,
    Error,
    { investmentScoreId: number; scoreOverride: number }
  >({
    mutationFn: async ({ investmentScoreId, scoreOverride }) => {
      const response = await scoresService.updateScoreOverride(
        investmentId,
        investmentScoreId,
        scoreOverride
      );
      // API wraps response in { success, data, meta } - unwrap it
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scoreKeys.investment(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Remove a score from an investment
 */
export function useRemoveScore(
  investmentId: number,
  options?: UseMutationOptions<DeleteScoreResponse, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<DeleteScoreResponse, Error, number>({
    mutationFn: async (investmentScoreId) => {
      const response = await scoresService.removeScore(investmentId, investmentScoreId);
      // API wraps response in { success, data, meta } - unwrap it
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scoreKeys.investment(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Clear all scores for an investment
 */
export function useClearAllScores(
  investmentId: number,
  options?: UseMutationOptions<{ message: string; investment_id: number }, Error, void>
) {
  const queryClient = useQueryClient();

  return useMutation<{ message: string; investment_id: number }, Error, void>({
    mutationFn: async () => {
      const response = await scoresService.clearAllScores(investmentId);
      // API wraps response in { success, data, meta } - unwrap it
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scoreKeys.investment(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}
